import { Skeleton, Stack } from '@mui/material';
import { useApolloClient } from '@apollo/client';
import { Header } from 'common/ui/Header';
import { useRecoilValue } from 'recoil';
import { integrationAtom } from 'services/integration/atom';
import {
  useGetCandidateProfileQuery,
  useAddCandidateProfilesToListMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { SearchCandidateProfileListsDocument } from 'generated-graphql/graphql';
import { extractQueryGetItem } from 'core/utils/apollo/extractGetItem';
import { getCandidateProfileSeparatorItems } from 'modules/candidate-profile/utils/separator-items/getCandidateProfileSeparatorItems';
import { getAtsCandidateUrl } from 'services/integration/utils/getAtsCandidateUrl';
import { getCandidateProfileActionItems } from 'modules/candidate-profile/utils/getCandidateProfileActionItems';
import { CandidateProfileDetailTabs } from 'modules/candidate-profile/tabs/CandidateProfileDetailTabs';
import { useSearchCandidateProfileSelected } from 'modules/candidate-profile/hooks/useSearchCandidateProfileSelected';
import { useAddToTalentListDrawer } from 'modules/candidate-profile/hooks/useAddToTalentListDrawer';
import {
  CandidateProfileDetailProps,
  CandidateProfileDetailType
} from 'modules/candidate-profile/types/CandidateProfileDetailProps';
import { useCandidateProfileDetailNavigation } from 'modules/candidate-profile/hooks/useCandidateProfileDetailNavigation';
import { useToast } from 'services/toast/hooks';
import { ToastType } from 'services/toast';
import { waitForOpenSearchAndWeaviate } from 'modules/candidate-profile/utils/waitForOpenSearchAndWeaviate';
import { getCandidateProfileDisplayName } from 'modules/candidate-profile/utils/getCandidateProfileDisplayName';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';

const CandidateProfileDetailSkeleton: React.FC = () => {
  return (
    <Stack gap="30px">
      <Skeleton variant="rectangular" width="700px" height={124} />
      <Stack
        direction="row"
        justifyContent="space-between"
        gap="10px"
        width="100%"
      >
        <Skeleton variant="rectangular" width="150px" height={58} />
        <Skeleton variant="rectangular" width="500px" height={58} />
      </Stack>
    </Stack>
  );
};

export const CandidateProfileDetail: React.FC<CandidateProfileDetailProps> = (
  props
) => {
  const { candidateProfileId } = props;
  const client = useApolloClient();
  const integration = useRecoilValue(integrationAtom);
  const { openAddToTalentListDrawer } = useAddToTalentListDrawer();
  const { breadcrumbs, handleBack } =
    useCandidateProfileDetailNavigation(props);
  const { showToast } = useToast();

  const { selectedCandidateIds, handleSelectionChange } =
    useSearchCandidateProfileSelected({});

  const { data } = useGetCandidateProfileQuery({
    variables: {
      id: candidateProfileId
    }
  });

  const [addCandidateProfilesToList] = useAddCandidateProfilesToListMutation();
  const isInviteToApplyHidden = Boolean(useFlag(Flags.HIDE_INVITE_TO_APPLY));

  const candidateProfile = extractQueryGetItem(data);

  const atsCandidateUrl = getAtsCandidateUrl({
    integrationCredentials: integration,
    atsCandidateId: candidateProfile?.atsCandidateId,
    atsApplicationId: candidateProfile?.latestAtsApplicationId
  });

  const handleInviteToggle = () => {
    if (!candidateProfileId) return;

    const isSelected = selectedCandidateIds.includes(candidateProfileId);
    const newSelectedIds = isSelected
      ? selectedCandidateIds.filter((id) => id !== candidateProfileId)
      : [...selectedCandidateIds, candidateProfileId];

    handleSelectionChange(newSelectedIds);
  };

  const handleAddToTalentList = async () => {
    switch (props.type) {
      case CandidateProfileDetailType.CANDIDATE_PROFILE_DETAILS:
        openAddToTalentListDrawer({
          selectedCandidateIds: [candidateProfileId]
        });
        break;
      case CandidateProfileDetailType.TALENT_POOL_ADD_CANDIDATES_CANDIDATE_PROFILE_DETAILS:
        await addCandidateProfilesToList({
          variables: {
            input: {
              candidateProfileIds: [candidateProfileId],
              candidateProfileListId: props.talentPoolId
            }
          }
        });

        await waitForOpenSearchAndWeaviate();

        await client.refetchQueries({
          include: [SearchCandidateProfileListsDocument]
        });

        showToast({
          title: 'Candidate added to talent list',
          type: ToastType.Success
        });
        break;
      case CandidateProfileDetailType.TALENT_POOL_CANDIDATE_PROFILE_DETAILS:
      case CandidateProfileDetailType.ANALYSE_CANDIDATE_PROFILE_DETAILS:
      case CandidateProfileDetailType.ANALYSE_TALENT_POOL_CANDIDATE_PROFILE_DETAILS:
        // Button should not show
        break;
    }
  };

  const displayName = candidateProfile
    ? getCandidateProfileDisplayName(candidateProfile)
    : '';

  return (
    <>
      {!data && <CandidateProfileDetailSkeleton />}
      {candidateProfile && (
        <Header
          breadcrumbs={breadcrumbs}
          currentBreadcrumb={displayName}
          title={displayName}
          items={getCandidateProfileSeparatorItems(candidateProfile)}
          onBack={handleBack}
          actions={getCandidateProfileActionItems({
            candidateProfileDetailProps: props,
            candidateProfile,
            atsCandidateUrl,
            provider: integration?.provider,
            isSelected: selectedCandidateIds.includes(candidateProfileId),
            isInviteToApplyHidden,
            onInviteToggle: handleInviteToggle,
            onAddToTalentList: handleAddToTalentList
          })}
        />
      )}
      <CandidateProfileDetailTabs
        candidateProfileId={candidateProfileId}
        candidateProfile={candidateProfile}
      />
    </>
  );
};
